import { useEffect, useState } from "react";
import { FileText, MessageSquare } from "lucide-react";
import { RagProvider, useRag } from "./RagContext";
import BandoSelector from "./components/BandoSelector";
import DocumentsSection from "./components/DocumentsSection";
import ChatSection from "./components/ChatSection";
import "./RagPage.css";

const TAB_STORAGE_KEY = "rag.activeTab";

function RagPageInner() {
    const { error, clearError, loadBandi } = useRag();
    const [activeTab, setActiveTab] = useState(() => {
        try {
            return localStorage.getItem(TAB_STORAGE_KEY) || "documenti";
        } catch {
            return "documenti";
        }
    });
    const [showInfo, setShowInfo] = useState(false);

    useEffect(() => {
        loadBandi();
    }, [loadBandi]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        try {
            localStorage.setItem(TAB_STORAGE_KEY, tab);
        } catch {
            // localStorage may be unavailable in private mode
        }
    };

    return (
        <div className="rag-page">
            <header className="rag-page__header">
                <div className="rag-page__header-text">
                    <div className="rag-page__title-row">
                        <h2 className="rag-page__title">RAG</h2>
                        <button
                            className="info-button-modern"
                            onClick={() => setShowInfo(!showInfo)}
                            title="Informazioni"
                        >
                            <span className="info-icon-text">i</span>
                        </button>
                    </div>
                    <p className="rag-page__subtitle">
                        Gestione documenti e chat AI sui bandi
                    </p>
                </div>
                <div className="rag-page__header-bando">
                    <BandoSelector />
                </div>
            </header>

            {/* Modal Informazioni */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                                </svg>
                            </div>
                            <h3>Informazioni sul sistema RAG</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Il sistema <strong>RAG</strong> (Retrieval-Augmented Generation) permette di
                                    interrogare in linguaggio naturale i documenti di un bando di gara. Carichi i PDF
                                    (bando, disciplinare, capitolato, allegati, offerte) e l'agente AI estrae il
                                    contesto pertinente dalle pagine del documento per rispondere alle tue domande,
                                    citando esattamente da dove proviene l'informazione.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Come funziona</h4>
                                <p>
                                    Ogni PDF caricato viene processato in tre fasi:
                                </p>
                                <ul>
                                    <li><strong>Parsing</strong> - estrazione del testo grezzo e identificazione di tabelle/sezioni</li>
                                    <li><strong>Chunking</strong> - suddivisione in segmenti coerenti (articoli, capi, allegati)</li>
                                    <li><strong>Embedding</strong> - vettorizzazione semantica per ricerca per similarità</li>
                                </ul>
                                <p>
                                    Quando fai una domanda, il retrieval ibrido (semantico + full-text) seleziona i
                                    chunk più rilevanti e li passa all'LLM come contesto per generare la risposta.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Come utilizzarlo</h4>
                                <ul>
                                    <li><strong>1. Seleziona un bando</strong> dal dropdown in alto, oppure crea un nuovo bando</li>
                                    <li><strong>2. Tab Documenti</strong> - carica PDF/TXT trascinandoli nell'area di drop. Lo stato passa da "In elaborazione" a "Pronto" al termine dell'ingestion (~30s-2min per documento).</li>
                                    <li><strong>3. Tab Chat</strong> - crea una nuova conversazione e fai domande in italiano</li>
                                    <li><strong>4. Citazioni</strong> - i marker <strong>[1]</strong>, <strong>[2]</strong>, ecc. nelle risposte sono cliccabili: aprono il PDF originale alla pagina del chunk citato, con highlight automatico sul testo.</li>
                                </ul>
                                <p className="modal-tip">
                                    💡 Le conversazioni sono <strong>scoped per bando</strong>: cambiando bando vedrai
                                    solo le conversazioni e i documenti di quel bando. Per usi diversi crea bandi separati.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="rag-page__error" role="alert">
                    <span>{error}</span>
                    <button onClick={clearError} className="rag-page__error-close" aria-label="Chiudi">
                        ×
                    </button>
                </div>
            )}

            <nav className="rag-tabs">
                <button
                    type="button"
                    className={`rag-tabs__btn ${activeTab === "documenti" ? "rag-tabs__btn--active" : ""}`}
                    onClick={() => handleTabChange("documenti")}
                >
                    <FileText size={16} />
                    <span>Documenti</span>
                </button>
                <button
                    type="button"
                    className={`rag-tabs__btn ${activeTab === "chat" ? "rag-tabs__btn--active" : ""}`}
                    onClick={() => handleTabChange("chat")}
                >
                    <MessageSquare size={16} />
                    <span>Chat</span>
                </button>
            </nav>

            <div className="rag-tabs__content">
                {activeTab === "documenti" ? <DocumentsSection /> : <ChatSection />}
            </div>
        </div>
    );
}

export default function RagPage() {
    return (
        <RagProvider>
            <RagPageInner />
        </RagProvider>
    );
}
